import { isEmpty, isNil, range } from 'lodash';
import moment from 'moment';
import { useCallback, useContext, useMemo } from 'react';
import { View as RNView, StyleSheet } from 'react-native';
import { RRule } from 'rrule';
import { MBView } from '@shared/components/MBView';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { MBIconV2 } from '@shared/components/image';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { ColorThemeContext, grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { NavStack } from '@shared/util/navigation';
import * as RecurrencePresets from '../../presets';
import { RecurrencePickerRouteNames } from '../RecurrencePickerRoutes';
import { DescriptiveRecurrenceHeader } from './DescriptiveRecurrenceHeader';
import { RepeatEveryLabel } from './RepeatEveryLabel';

const NUM_DAYS = 31;
const NUM_DAYS_IN_WEEK = 7;

interface Props {
  rruleString: string | undefined;
  onChange(rule: RRule | undefined): void;
  nav: NavStack<RecurrencePickerRouteNames>;
}

export function MonthlyRecurrencePicker({ rruleString, onChange, nav }: Props) {
  const colors = useContext(ColorThemeContext);
  const rule = useMemo(
    () => (!isNil(rruleString) ? RRule.fromString(rruleString) : undefined),
    [rruleString],
  );

  const currentDayIndices = rule?.options.bymonthday;
  const handleChange = useCallback(
    (bymonthday: number) => {
      // @ts-expect-error -- strict migration
      onChange(RecurrencePresets.monthly(bymonthday, rule.options.interval));
    },
    [rule, onChange],
  );

  const openIntervalPicker = useCallback(() => {
    nav.push({ name: 'selectMonthlyInterval' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {!isNil(rule) && <DescriptiveRecurrenceHeader rule={rule} />}
      <RepeatEveryLabel
        intervalValue={rule?.options.interval ?? 1}
        intervalPluralStringKey={'card.blocks.recurrenceInput.monthly.xMonths'}
        isPressable={!rule}
        onPress={openIntervalPicker}
      />
      <View paddingHorizontal={3}>
        <Text size="body2" weight="semibold">
          {i18n('card.blocks.recurrenceInput.monthly.monthDay.title')}
        </Text>
      </View>
      <View paddingHorizontal={3} paddingTop={2}>
        {!isEmpty(rule?.options.bymonthday) && (
          <View row centerVertically paddingBottom={2}>
            <MBIconV2 name="calendar" size={grid(4)} color={colors.accent} />
            <View paddingLeft={2} />
            <Text size="body1">
              {(rule?.options.bymonthday ?? []).map(moment.localeData().ordinal).join(', ')}
            </Text>
          </View>
        )}

        <RNView style={styles.grid}>
          {range(Math.ceil(NUM_DAYS / NUM_DAYS_IN_WEEK)).map((weekIndex, index) => (
            <RNView key={index} style={styles.row}>
              {range(NUM_DAYS_IN_WEEK).map(dayIndex => {
                const monthDay = weekIndex * NUM_DAYS_IN_WEEK + dayIndex + 1;
                if (monthDay > NUM_DAYS) {
                  return <RNView key={dayIndex} style={styles.cell} />;
                }
                const selected = !!currentDayIndices?.includes(monthDay);
                return (
                  <MBTouchableOpacity
                    key={dayIndex}
                    style={styles.cell}
                    onPress={() => handleChange(monthDay)}
                  >
                    <MBView
                      center
                      style={[
                        styles.cellContent,
                        selected && { backgroundColor: colors.foregroundActive },
                      ]}
                    >
                      <Text
                        size="body1"
                        weight="semibold"
                        color={selected ? colors.background : colors.foregroundActive}
                      >
                        {monthDay}
                      </Text>
                    </MBView>
                  </MBTouchableOpacity>
                );
              })}
            </RNView>
          ))}
        </RNView>
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  grid: { flexDirection: 'column' },
  row: { flexDirection: 'row' },
  cell: {
    flex: 1,
    height: grid(5),
    justifyContent: 'center',
    alignItems: 'center',
  },
  cellContent: {
    width: grid(5),
    height: grid(5),
    borderRadius: grid(2.5),
    overflow: 'hidden',
  },
});
